import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, Wifi, Search, User, Mail, Phone, 
  Building, Globe, CheckCircle2, AlertCircle
} from 'lucide-react';
import { getAuthHeaders, getUser } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminNFCWriter = () => {
  const navigate = useNavigate();
  const currentUser = getUser();
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [nfcSupported, setNfcSupported] = useState(false);
  const [writing, setWriting] = useState(false);
  const [writeStatus, setWriteStatus] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if admin (you can implement proper admin check)
    // For now, just check if user exists
    if (!currentUser) {
      navigate('/auth');
      return;
    }

    checkNFCSupport();
    loadUsers();
  }, []);

  const checkNFCSupport = () => {
    if ('NDEFReader' in window) {
      setNfcSupported(true);
    } else {
      setNfcSupported(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/all`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleWriteToNFC = async () => {
    if (!nfcSupported) {
      toast.error('NFC not supported on this device');
      return;
    }

    if (!selectedUser) {
      toast.error('Please select a user first');
      return;
    }

    try {
      setWriting(true);
      setWriteStatus('Initializing NFC writer...');

      const ndef = new window.NDEFReader();
      
      setWriteStatus('Tap your NFC card to the device now...');

      // Create vCard data
      const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${selectedUser.name || ''}
ORG:${selectedUser.businessName || ''}
TITLE:${selectedUser.title || ''}
TEL;TYPE=WORK:${selectedUser.phone || ''}
EMAIL:${selectedUser.email || ''}
URL:${selectedUser.website || ''}
URL:${window.location.origin}/profile/${selectedUser.id}
NOTE:DigiConnect - Digital Business Card
END:VCARD`;

      // Write to NFC card
      await ndef.write({
        records: [
          { 
            recordType: "text", 
            data: vCardData,
            encoding: "utf-8",
            language: "en"
          },
          { 
            recordType: "url", 
            data: `${window.location.origin}/profile/${selectedUser.id}` 
          }
        ]
      });

      setWriteStatus('✅ Successfully written to NFC card!');
      toast.success(`✅ NFC Card Written Successfully for ${selectedUser.name}!`);
      
      // Log the write
      console.log('NFC Write Success:', {
        userId: selectedUser.id,
        userName: selectedUser.name,
        timestamp: new Date().toISOString()
      });

      // Reset after 3 seconds
      setTimeout(() => {
        setWriting(false);
        setWriteStatus('');
      }, 3000);

    } catch (error) {
      console.error('NFC write error:', error);
      setWriteStatus('❌ Failed to write NFC card');
      
      if (error.name === 'AbortError') {
        toast.error('NFC write cancelled or timed out');
      } else if (error.name === 'NotAllowedError') {
        toast.error('NFC permission denied. Please enable NFC in browser settings.');
      } else if (error.name === 'NotSupportedError') {
        toast.error('NFC not supported on this device');
      } else {
        toast.error('Failed to write NFC card. Make sure NFC is enabled and card is near device.');
      }

      setTimeout(() => {
        setWriting(false);
        setWriteStatus('');
      }, 3000);
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.businessName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <p className="text-slate-300">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/browse')}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              🔒 Admin NFC Card Writer
            </h1>
            <p className="text-slate-400 mt-1">Write customer contacts to NFC cards before shipping</p>
          </div>
        </div>

        {/* NFC Support Warning */}
        {!nfcSupported && (
          <Card className="p-4 bg-red-500/20 backdrop-blur-sm border border-red-500/50 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-red-300 font-semibold">NFC Not Supported</p>
                <p className="text-sm text-red-200">
                  This device/browser doesn't support Web NFC API. Please use Android Chrome or Samsung Internet.
                </p>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: User Selection */}
          <div>
            <Card className="p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700">
              <h2 className="text-xl font-bold text-white mb-4">Select Customer</h2>
              
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, email, or company..."
                    className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              {/* Users List */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">No users found</p>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                      className={`p-4 rounded-lg cursor-pointer transition-all ${
                        selectedUser?.id === user.id
                          ? 'bg-cyan-500/20 border-2 border-cyan-500'
                          : 'bg-slate-700/30 border border-slate-600 hover:border-cyan-500'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-slate-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold">{user.name}</h3>
                          {user.businessName && (
                            <p className="text-sm text-slate-400">{user.businessName}</p>
                          )}
                          <p className="text-xs text-slate-500 truncate">{user.email}</p>
                        </div>
                        {selectedUser?.id === user.id && (
                          <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* Right: Selected User & NFC Write */}
          <div>
            {selectedUser ? (
              <Card className="p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Contact Information</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-xs text-slate-400">Name</p>
                      <p className="text-white font-semibold">{selectedUser.name}</p>
                    </div>
                  </div>

                  {selectedUser.businessName && (
                    <div className="flex items-center gap-3">
                      <Building className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="text-xs text-slate-400">Company</p>
                        <p className="text-white">{selectedUser.businessName}</p>
                      </div>
                    </div>
                  )}

                  {selectedUser.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="text-xs text-slate-400">Email</p>
                        <p className="text-white break-all">{selectedUser.email}</p>
                      </div>
                    </div>
                  )}

                  {selectedUser.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="text-xs text-slate-400">Phone</p>
                        <p className="text-white">{selectedUser.phone}</p>
                      </div>
                    </div>
                  )}

                  {selectedUser.website && (
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="text-xs text-slate-400">Website</p>
                        <p className="text-white break-all">{selectedUser.website}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Profile URL */}
                <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600 mb-6">
                  <p className="text-xs text-slate-400 mb-1">Profile URL (will be written to NFC):</p>
                  <p className="text-sm text-cyan-400 break-all">
                    {window.location.origin}/profile/{selectedUser.id}
                  </p>
                </div>

                {/* Write Button */}
                <Button
                  onClick={handleWriteToNFC}
                  disabled={!nfcSupported || writing}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-6 text-lg font-semibold shadow-lg disabled:opacity-50"
                >
                  <Wifi className="w-5 h-5 mr-2" />
                  {writing ? 'Writing to NFC Card...' : 'Write to NFC Card'}
                </Button>

                {/* Status */}
                {writeStatus && (
                  <div className={`mt-4 p-4 rounded-lg ${
                    writeStatus.includes('✅') 
                      ? 'bg-green-500/20 border border-green-500/50' 
                      : writeStatus.includes('❌')
                      ? 'bg-red-500/20 border border-red-500/50'
                      : 'bg-blue-500/20 border border-blue-500/50'
                  }`}>
                    <p className={`text-center font-semibold ${
                      writeStatus.includes('✅') 
                        ? 'text-green-300' 
                        : writeStatus.includes('❌')
                        ? 'text-red-300'
                        : 'text-blue-300'
                    }`}>
                      {writeStatus}
                    </p>
                  </div>
                )}

                {/* Instructions */}
                <div className="mt-6 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
                  <h3 className="text-sm font-semibold text-slate-300 mb-2">📝 Instructions:</h3>
                  <ol className="text-xs text-slate-400 space-y-1 list-decimal list-inside">
                    <li>Select customer from the list</li>
                    <li>Verify contact information is correct</li>
                    <li>Click "Write to NFC Card" button</li>
                    <li>Tap the NFC card to your device</li>
                    <li>Wait for success confirmation</li>
                    <li>Test the card by scanning it</li>
                    <li>Ship the card to customer</li>
                  </ol>
                </div>
              </Card>
            ) : (
              <Card className="p-12 bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-center">
                <User className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No User Selected</h3>
                <p className="text-slate-400">Select a customer from the list to write their contact info to an NFC card</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNFCWriter;
